import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { ArrowUpIcon, ArrowUpRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { footerInfo, studios } from '../data/landing';
import { BrandLogo } from './BrandLogo';
import { FooterNetwork } from './FooterNetwork';
function StudioAddress() {
  return <address data-pixel-avoid className="space-y-6 not-italic md:space-y-8" data-id="element-123" data-mp="3uf0mr7ucs6s8">
      {studios.map(studio => <div key={studio.city} className="text-[15px] leading-[1.4]" data-id="element-124" data-mp="3991alritibr">
          <p className="mb-1 text-[12px] font-medium uppercase tracking-[0.04em] text-white/60" data-id="element-125" data-mp="36d3qyjl4vnu">{studio.city}</p>
          {studio.lines.map(line => <p key={line} data-id="element-126" data-mp="3qcdpgrewxtnn">{line}</p>)}
          <a href={`tel:${studio.phone.replace(/[^+\d]/g, '')}`} className="mt-2 block text-white/50 transition-colors duration-150 hover:text-white" data-id="element-127" data-mp="3qf9s2t1ph6if">
            {studio.phone}
          </a>
        </div>)}
      <a href={`mailto:${footerInfo.email}`} className="-mt-4 block text-[15px] transition-opacity duration-150 hover:opacity-70 md:-mt-6" data-id="element-128" data-mp="3lalgfg6ys7uh">
        {footerInfo.email}
      </a>
    </address>;
}
export function Footer() {
  const ref = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();

  // Strong parallax: content rises from far below as the footer scrolls into view.
  const {
    scrollYProgress
  } = useScroll({
    target: ref,
    offset: ['start end', 'end end']
  });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['-45%', '0%']);
  const opacity = useTransform(scrollYProgress, [0, 0.6], reduce ? [1, 1] : [0.2, 1]);
  return <footer ref={ref} id="contact" className="relative overflow-hidden bg-black text-white" data-id="element-129" data-mp="ctgyh86nu0ph">
      {/* Quiet pixel neural network in the empty space; fades around every [data-pixel-avoid] element */}
      <FooterNetwork className="z-0" data-id="element-130" data-mp="mspppta55185" />
      <motion.div style={{
      y,
      opacity
    }} className="relative z-10 flex min-h-[100svh] flex-col pt-24 will-change-transform md:min-h-0 md:pt-28" data-id="element-131" data-mp="mtrawqkvf43l">
        <div className="flex flex-1 flex-col px-page" data-id="element-132" data-mp="yv93jr68lwup">
          {/* Top: studios / logo — from tablet up; on phones the address moves below the marquee */}
          <div className="hidden items-start justify-between gap-8 md:flex" data-id="element-133" data-mp="18k0sdqg0411e">
            <div data-id="element-134" data-mp="18m3sx4es5ckd">
              <StudioAddress data-id="element-135" data-mp="1clmabq7e75ig" />
            </div>

            <a href="#top" aria-label="Back to top" data-pixel-avoid className="hidden shrink-0 text-white md:block" data-id="element-136" data-mp="1ckt2sn0m0bz9">
              <BrandLogo className="h-12 w-12" data-id="element-137" data-mp="1hiul35q7o79o" />
            </a>
          </div>

          {/* Tagline + socials */}
          <div className="mt-auto flex flex-col gap-7 md:flex-row md:items-end md:justify-between md:gap-8 md:pt-44" data-id="element-138" data-mp="1mhxpqp2a4bh4">
            <div data-pixel-avoid data-id="element-139" data-mp="3obeunkz0yy86">
              <p className="max-w-[640px] font-serif text-[36px] font-normal leading-[1.08] tracking-[-0.01em] md:text-[48px]" data-id="element-140" data-mp="3oc6oorcz4nwc">
                {footerInfo.tagline}
              </p>
              <Link to="/contact" className="group mt-4 inline-flex items-center gap-3 md:mt-8 whitespace-nowrap rounded-control border border-white/80 px-6 py-3 text-[14px] uppercase tracking-[0.04em] text-white transition-colors duration-150 hover:border-white hover:bg-white hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black" data-id="element-141" data-mp="2afdidco5p8os">
                Contact us
                <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-142" data-mp="2pc4l9vx1h2j5" />
              </Link>
            </div>
            <ul data-pixel-avoid className="flex gap-5" data-id="element-143" data-mp="2fgayzktipuo6">
              {footerInfo.socials.map(s => <li key={s.label} data-id="element-144" data-mp="18vrxxs9o2abg">
                  <a href={s.href} className="text-[15px] transition-opacity duration-150 hover:opacity-70" data-id="element-145" data-mp="13ys20a6j6ny2">
                    {s.label}
                  </a>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Phones: address first, then the marquee beneath it */}
        <div className="flex flex-col" data-id="element-146" data-mp="2iw45ks2ndglr">
        {/* Marquee */}
        <div data-pixel-avoid className="mt-14 overflow-hidden md:mt-20" aria-hidden="true" data-id="element-147" data-mp="2nu5ond02pfx8">
          <motion.div className="flex w-max whitespace-nowrap" animate={reduce ? undefined : {
            x: ['0%', '-50%']
          }} transition={{
            duration: 28,
            ease: 'linear',
            repeat: Infinity
          }} data-id="element-148" data-mp="3bjqk2vdey5ne">
            {[0, 1].map(copy => <div key={copy} className="flex items-center" data-id="element-149" data-mp="31l13d5x96r8q">
                {[0, 1].map(i => <span key={i} className="flex items-center text-[15vw] font-normal leading-[0.9] tracking-[-0.03em] text-white md:text-[176px]" data-id="element-150" data-mp="36isqznqv7r74">
                    {footerInfo.marquee}
                    <span className="mx-[0.35em] inline-block h-[0.72em] w-[0.72em] bg-white" data-id="element-151" data-mp="1nlpjutnet7r5" />
                  </span>)}
              </div>)}
          </motion.div>
        </div>

        <div className="order-first px-page pt-16 md:hidden" data-id="element-152" data-mp="1wn5qqrf0mrqm">
          <StudioAddress data-id="element-153" data-mp="1wk9tz6ewm93r" />
        </div>
        </div>

        <div className="mt-10 border-t border-white/15 md:mt-8" data-id="element-154" data-mp="1rom1ah17qxti" />

        {/* Bottom bar */}
        <div data-pixel-avoid className="flex flex-col gap-2.5 px-page pb-7 pt-6 text-[12px] md:pb-8 md:pt-8 md:flex-row md:text-[13px] md:items-center md:justify-between md:gap-4 md:py-8" data-id="element-155" data-mp="13z06s6vsd6ru">
          <a href="#top" className="group inline-flex items-center gap-2 whitespace-nowrap transition-opacity duration-150 hover:opacity-70" data-id="element-156" data-mp="1do1jno71b81l">
            <ArrowUpIcon className="h-3.5 w-3.5 transition-transform duration-150 group-hover:-translate-y-0.5" aria-hidden="true" data-id="element-157" data-mp="l77ekeojjpl6" />
            Back to top
          </a>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-white/75" data-id="element-158" data-mp="2gb5fvuwx3nnh">
            <span data-id="element-159" data-mp="2l9gndyzqpm2j">© KAYS VENTURES FZCO 2018–2026 · laebel.co · laebel.io</span>
            {footerInfo.legal.map(l => <Link key={l.label} to={l.href} className="transition-colors duration-150 hover:text-white" data-id="element-160" data-mp="2q06vhjzjw30x">
                {l.label}
              </Link>)}
          </div>
        </div>
      </motion.div>
    </footer>;
}